import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { blogPosts } from '../data/blogData';
import { ArrowRight, Clock, Calendar, Menu, X } from 'lucide-react';

const Blog = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex justify-between items-center">
          <button onClick={() => navigate('/')} className="flex items-center gap-3">
            <img src="/assets/bragbox-hero.png" alt="BragBox Logo" className="h-10 md:h-12" />
          </button>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-2">
            <button onClick={() => navigate('/blog')} className="px-3 py-2 rounded-lg font-medium text-sm transition-colors hover:bg-gray-50" style={{ color: 'var(--navy-900)' }}>Blog</button>
            <button onClick={() => navigate('/pricing')} className="px-3 py-2 rounded-lg font-medium text-sm transition-colors hover:bg-gray-50" style={{ color: 'var(--navy-900)' }}>Pricing</button>
            <button onClick={() => navigate('/login?existing=true')} className="px-3 py-2 rounded-lg font-medium text-sm transition-colors" style={{ color: 'var(--navy-900)' }}>Log In</button>
            <button
              onClick={() => navigate('/login?signup=true')}
              className="px-5 py-2.5 rounded-lg font-semibold text-sm transition-all shadow-md hover:shadow-lg inline-flex items-center"
              style={{ backgroundColor: 'var(--navy-900)', color: 'white' }}
            >
              Start free
            </button>
          </div>

          {/* Mobile: CTA + Hamburger */}
          <div className="flex md:hidden items-center gap-2">
            <button
              onClick={() => navigate('/login?signup=true')}
              className="px-4 py-2 rounded-lg font-semibold text-sm inline-flex items-center"
              style={{ backgroundColor: 'var(--navy-900)', color: 'white' }}
            >
              Start free
            </button>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2 rounded-lg" style={{ color: 'var(--navy-900)' }}>
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 px-4 py-3 flex flex-col gap-1">
            <button onClick={() => { navigate('/blog'); setMobileMenuOpen(false); }} className="text-left px-3 py-3 rounded-lg font-medium text-sm hover:bg-gray-50" style={{ color: 'var(--navy-900)' }}>Blog</button>
            <button onClick={() => { navigate('/pricing'); setMobileMenuOpen(false); }} className="text-left px-3 py-3 rounded-lg font-medium text-sm hover:bg-gray-50" style={{ color: 'var(--navy-900)' }}>Pricing</button>
            <button onClick={() => { navigate('/login?existing=true'); setMobileMenuOpen(false); }} className="text-left px-3 py-3 rounded-lg font-medium text-sm hover:bg-gray-50" style={{ color: 'var(--navy-900)' }}>Log In</button>
          </div>
        )}
      </header>

      {/* Blog header */}
      <div className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-12">
          <h1 className="text-4xl font-bold mb-3" style={{ fontFamily: 'Manrope', color: 'var(--navy-900)' }}>BragBox Blog</h1>
          <p className="text-lg text-gray-600">Career advice, promotion tips, and strategies for professionals who want to get credit for the work they do.</p>
        </div>
      </div>

      {/* Popular comparisons - internal SEO linking */}
      <div className="max-w-4xl mx-auto px-4 md:px-8 pt-8">
        <div className="rounded-xl p-5 md:p-6" style={{ backgroundColor: 'var(--cream-100, #f8f1e4)', border: '1px solid var(--cream-300, #e6d2ab)' }}>
          <p className="text-sm font-semibold mb-3 uppercase tracking-wide" style={{ color: 'var(--navy-900)' }}>Popular comparisons</p>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => navigate('/blog/bragbox-vs-teal')} className="px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors hover:bg-white" style={{ color: 'var(--navy-900)', borderColor: 'var(--cream-300, #e6d2ab)', backgroundColor: 'white' }}>BragBox vs Teal</button>
            <button onClick={() => navigate('/blog/bragbox-vs-huntr')} className="px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors hover:bg-white" style={{ color: 'var(--navy-900)', borderColor: 'var(--cream-300, #e6d2ab)', backgroundColor: 'white' }}>BragBox vs Huntr</button>
            <button onClick={() => navigate('/blog/best-brag-book-tools-2026')} className="px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors hover:bg-white" style={{ color: 'var(--navy-900)', borderColor: 'var(--cream-300, #e6d2ab)', backgroundColor: 'white' }}>Best Brag Book Tools 2026</button>
          </div>
        </div>
      </div>

      {/* Posts list */}
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-12">
        <div className="space-y-8">
          {blogPosts.map((post) => (
            <article
              key={post.id}
              className="border border-gray-200 rounded-xl p-6 md:p-8 hover:shadow-md transition-all cursor-pointer group"
              style={{ '--hover-border': 'var(--cream-400)' }}
              onClick={() => navigate(`/blog/${post.slug}`)}
            >
              <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                <span className="flex items-center gap-1.5">
                  <Calendar size={14} />
                  {post.date}
                </span>
                <span className="flex items-center gap-1.5">
                  <Clock size={14} />
                  {post.readTime}
                </span>
              </div>
              <h2 className="text-xl md:text-2xl font-bold mb-3 transition-colors" style={{ color: 'var(--navy-900)' }}>
                {post.title}
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">{post.excerpt}</p>
              <span className="inline-flex items-center gap-1.5 font-medium text-sm group-hover:gap-2.5 transition-all" style={{ color: 'var(--cream-600)' }}>
                Read article <ArrowRight size={16} />
              </span>
            </article>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-16 rounded-2xl p-8 text-center" style={{ backgroundColor: 'var(--cream-100)', border: '1px solid var(--cream-300)' }}>
          <h3 className="text-2xl font-bold mb-3" style={{ fontFamily: 'Manrope', color: 'var(--navy-900)' }}>Do not leave the blog with the same review bottleneck.</h3>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">BragBox captures your wins as they happen and polishes them into review-ready language. Free to start, no credit card at signup.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => navigate('/login?signup=true')}
              className="font-semibold px-6 py-3 rounded-lg transition-all shadow-md hover:shadow-lg inline-flex items-center justify-center"
              style={{ backgroundColor: 'var(--navy-900)', color: 'white' }}
            >
              Start free
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-4">Already have an account? <button onClick={() => navigate('/login')} className="underline">Log in</button>.</p>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-12 px-4 md:px-8 border-t border-gray-200">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-3">
              <img src="/assets/bragbox-hero.png" alt="BragBox Logo" className="h-8" />
            </div>
            <div className="flex items-center gap-6 text-sm text-gray-500">
              <button onClick={() => navigate('/blog')} className="hover:text-gray-700">Blog</button>
              <button onClick={() => navigate('/pricing')} className="hover:text-gray-700">Pricing</button>
              <button onClick={() => navigate('/contact')} className="hover:text-gray-700">Contact</button>
              <a href="https://www.linkedin.com/company/bragboxai/" target="_blank" rel="noopener noreferrer" className="hover:text-gray-700">LinkedIn</a>
              <a href="https://x.com/bragbox_ai" target="_blank" rel="noopener noreferrer" className="hover:text-gray-700">X</a>
            </div>
          </div>
          <div className="text-center mt-8">
            <p className="text-gray-400 text-sm">© 2026 BragBox. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Blog;
